import { Suspense, useEffect, useState } from "react";
import {
  ToasterLazy as LazyToaster,
  SonnerLazy as LazySonner,
} from "@/lib/prerenderedAppChunks";

/**
 * Defers mounting of the shadcn `<Toaster>` and Sonner `<Toaster>` until
 * one of:
 *   (a) the first user input (pointerdown / keydown / touchstart), OR
 *   (b) a browser-idle window after first paint.
 *
 * Why:
 *   - PSI's homepage audit runs headless with no user interaction, so the
 *     `sonner-*.js` chunk (and its transitive deps) stays off the initial
 *     critical chain — eliminating the ~800–1400 ms "Maximum critical
 *     path latency" PSI was attributing to it.
 *   - Real users still get the renderer mounted before they can plausibly
 *     trigger a toast (mount fires on the very first interaction).
 *
 * Safety:
 *   - `toast(...)` from `sonner` and `toast(...)` / `useToast()` from
 *     `@/hooks/use-toast` both write to module-level state. When the
 *     renderer mounts later it subscribes to that state and any queued
 *     toast is rendered. No call site needs to change.
 */
export function LazyToastHost() {
  const [shouldMount, setShouldMount] = useState(false);

  useEffect(() => {
    if (shouldMount) return;
    let cancelled = false;

    const mount = () => {
      if (cancelled) return;
      setShouldMount(true);
    };

    const events = ["pointerdown", "keydown", "touchstart"] as const;
    const opts: AddEventListenerOptions = { once: true, passive: true, capture: true };
    for (const evt of events) window.addEventListener(evt, mount, opts);

    // Phase 4 — programmatic-toast trigger. `use-toast`'s module-level
    // `toast(...)` dispatches this event before queueing the toast, so
    // the renderer mounts in the same tick and picks up the queued
    // entry via its module-state subscription. This replaces the
    // previous idle-timeout fallback that was pulling toast chunks
    // into the PSI measurement window on every fresh visit.
    const onToastRequested = () => mount();
    window.addEventListener("youtemp:toast-requested", onToastRequested, {
      once: true,
    });

    return () => {
      cancelled = true;
      for (const evt of events) window.removeEventListener(evt, mount, opts);
      window.removeEventListener(
        "youtemp:toast-requested",
        onToastRequested,
      );
    };
  }, [shouldMount]);

  if (!shouldMount) return null;

  return (
    <Suspense fallback={null}>
      <LazyToaster />
      <LazySonner />
    </Suspense>
  );
}